import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import GuineaMap from "../components/GuineaMap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";
import { TrendingUp, Users, Building2, FileText, Filter, Download, UserCheck, Briefcase, DollarSign, Clock, GitCompare, Map, Calendar, Factory, FileSpreadsheet, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import AdvancedFilters from "../components/AdvancedFilters";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#1B4D3E", "#D4AF37", "#0EA5E9", "#C62828", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981"];

const REGIONS = [
  "Conakry",
  "Boké",
  "Kindia",
  "Mamou",
  "Labé",
  "Faranah",
  "Kankan",
  "Nzérékoré",
];

const SECTEURS = [
  "BTP, Génie Civil, Immobilier",
  "Agriculture, Agro-industrie, Elevage, Pêche",
  "Ambassade",
  "Automobile et Mécanique Générale",
  "Banques, IMF, Monnaies Electroniques, Assurances",
  "Diamantaires, Orpailleurs et Assimilés",
  "Enseignement Privé",
  "Environnement, Assainissement, Bois",
  "EPA",
  "Hydrocarbure et énergie",
  "Industrie Manufacturière, Commerce",
  "Institution",
  "Mines et Carrières",
  "Prestation de Services, Bureau d'Etude Technique",
  "SA Etatique",
  "Santé",
  "Sécurité Privée",
  "TIC, Télécom, Economie numérique, Presse",
  "Tourisme, Artisanat, Boulangerie, Patisserie",
  "Transport, Transit, Consignation",
];

export default function StatisticsPage() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [compareMode, setCompareMode] = useState("region"); // region, period, secteur
  const [selectedRegions, setSelectedRegions] = useState(["Conakry"]);
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedSecteurs, setSelectedSecteurs] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);
  const [selectedRegionOnMap, setSelectedRegionOnMap] = useState(null);
  const [globalFilters, setGlobalFilters] = useState({});

  // Gestionnaire des filtres avancés
  const handleGlobalFiltersChange = (filters) => {
    setGlobalFilters(filters);
    // Appliquer les filtres aux données si nécessaire
    if (filters.region && filters.region !== "all") {
      setSelectedRegions([filters.region]);
      setCompareMode("region");
    }
    if (filters.secteur && filters.secteur !== "all") {
      setSelectedSecteurs([filters.secteur]);
      setCompareMode("secteur");
    }
    if (filters.annee && filters.annee !== "all") {
      setSelectedYears([filters.annee]);
      setCompareMode("period");
    }
  };

  // Export handlers
  const handleExport = async (type, format) => {
    setExporting(true);
    try {
      const endpoint = type === "statistics" 
        ? `${API_URL}/export/admin/statistics?format=${format}`
        : `${API_URL}/export/admin/bilans?format=${format}`;
      
      const response = await axios.get(endpoint, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = type === "statistics" 
        ? `statistiques_regions_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`
        : `bilans_export_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export ${format.toUpperCase()} téléchargé avec succès`);
    } catch (error) {
      console.error(/* PROD */ "Export failed:", error);
      toast.error("Erreur lors de l'export. Veuillez réessayer.");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (compareMode === "region" && selectedRegions.length > 0) {
      fetchComparisonData();
    } else if (compareMode === "period" && selectedYears.length > 0) {
      fetchComparisonData();
    } else if (compareMode === "secteur" && selectedSecteurs.length > 0) {
      fetchComparisonData();
    }
  }, [compareMode, selectedRegions, selectedYears, selectedSecteurs]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [statsRes, bilansRes] = await Promise.all([
        axios.get(`${API_URL}/statistics/national`),
        axios.get(`${API_URL}/statistics/bilans`)
      ]);
      
      setStats(statsRes.data);
      const years = bilansRes.data.available_years || [];
      setAvailableYears(years);
      if (years.length > 0) {
        setSelectedYears([years[0].toString()]);
      }
    } catch (error) {
      console.error(/* PROD */ "Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComparisonData = async () => {
    try {
      let data = [];
      
      if (compareMode === "region") {
        const promises = selectedRegions.map(region =>
          axios.get(`${API_URL}/statistics/bilans?region=${region}`)
        );
        const responses = await Promise.all(promises);
        data = responses.map((res, idx) => ({
          name: selectedRegions[idx],
          ...res.data,
          color: COLORS[idx % COLORS.length]
        }));
      } else if (compareMode === "period") {
        const promises = selectedYears.map(year =>
          axios.get(`${API_URL}/statistics/bilans?annee=${year}`)
        );
        const responses = await Promise.all(promises);
        data = responses.map((res, idx) => ({
          name: selectedYears[idx],
          ...res.data,
          color: COLORS[idx % COLORS.length]
        }));
      } else if (compareMode === "secteur") {
        const promises = selectedSecteurs.map(secteur =>
          axios.get(`${API_URL}/statistics/bilans?secteur=${encodeURIComponent(secteur)}`)
        );
        const responses = await Promise.all(promises);
        data = responses.map((res, idx) => ({
          name: selectedSecteurs[idx],
          ...res.data,
          color: COLORS[idx % COLORS.length]
        }));
      }
      
      setComparisonData(data);
    } catch (error) {
      console.error(/* PROD */ "Failed to fetch comparison data:", error);
    }
  };

  const toggleRegion = (region) => {
    setSelectedRegions(prev => {
      if (prev.includes(region)) {
        return prev.filter(r => r !== region);
      } else if (prev.length < 4) {
        return [...prev, region];
      }
      return prev;
    });
  };

  const toggleYear = (year) => {
    setSelectedYears(prev => {
      if (prev.includes(year)) {
        return prev.filter(y => y !== year);
      } else if (prev.length < 4) {
        return [...prev, year];
      }
      return prev;
    });
  };

  const toggleSecteur = (secteur) => {
    setSelectedSecteurs(prev => {
      if (prev.includes(secteur)) {
        return prev.filter(s => s !== secteur);
      } else if (prev.length < 4) {
        return [...prev, secteur];
      }
      return prev;
    });
  };

  const handleRegionClick = (regionName) => {
    setSelectedRegionOnMap(regionName);
    if (compareMode === "region" && !selectedRegions.includes(regionName)) {
      toggleRegion(regionName);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 pb-16">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-32 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Prepare comparison chart data
  const effectifsCompareData = comparisonData.map(d => ({
    name: d.name,
    "Guinéens H": d.total_guineens_hommes || 0,
    "Guinéens F": d.total_guineens_femmes || 0,
    "Étrangers H": d.total_etrangers_hommes || 0,
    "Étrangers F": d.total_etrangers_femmes || 0,
  }));

  const contratsCompareData = comparisonData.map(d => ({
    name: d.name,
    CDI: d.total_cdi || 0,
    CDD: d.total_cdd || 0,
  }));

  const tauxCompareData = comparisonData.map(d => ({
    name: d.name,
    "Taux Rotation": d.avg_taux_rotation || 0,
    "Taux Absentéisme": d.avg_taux_absenteisme || 0,
  }));

  const categoriesCompareData = comparisonData.map(d => ({
    name: d.name,
    Cadres: d.total_cadres || 0,
    "Agents Maîtrise": d.total_agents_maitrise || 0,
    Employés: d.total_employes || 0,
    Ouvriers: d.total_ouvriers || 0,
  }));

  const mouvementsCompareData = comparisonData.map(d => ({
    name: d.name,
    Embauches: d.total_embauches || 0,
    Licenciements: d.total_licenciements || 0,
    Démissions: d.total_demissions || 0,
  }));

  // Radar chart data
  const radarData = [
    { subject: "CDI", fullMark: 100 },
    { subject: "CDD", fullMark: 100 },
    { subject: "Rotation", fullMark: 100 },
    { subject: "Absentéisme", fullMark: 100 },
    { subject: "Embauches", fullMark: 100 },
  ].map(item => {
    const result = { ...item };
    comparisonData.forEach((d, idx) => {
      if (item.subject === "CDI") result[d.name] = Math.min((d.total_cdi || 0) / 10, 100);
      if (item.subject === "CDD") result[d.name] = Math.min((d.total_cdd || 0) / 10, 100);
      if (item.subject === "Rotation") result[d.name] = Math.min((d.avg_taux_rotation || 0) * 5, 100);
      if (item.subject === "Absentéisme") result[d.name] = Math.min((d.avg_taux_absenteisme || 0) * 5, 100);
      if (item.subject === "Embauches") result[d.name] = Math.min((d.total_embauches || 0) / 5, 100);
    });
    return result;
  });

  return (
    <div className="space-y-6 pb-16" data-testid="statistics-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Statistiques Comparatives des Bilans Sociaux
          </h1>
          <p className="text-muted-foreground mt-1">
            Comparez les données par région, période ou secteur d'activité
          </p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-fit" disabled={exporting} data-testid="export-stats-btn">
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                )}
                Exporter Statistiques
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport("statistics", "excel")} data-testid="export-stats-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("statistics", "pdf")} data-testid="export-stats-pdf">
                <FileDown className="w-4 h-4 mr-2" />
                PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="default" className="w-fit bg-primary" disabled={exporting} data-testid="export-bilans-btn">
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Exporter Bilans
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport("bilans", "excel")} data-testid="export-bilans-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("bilans", "pdf")} data-testid="export-bilans-pdf">
                <FileDown className="w-4 h-4 mr-2" />
                PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filtres avancés globaux */}
      <Card>
        <CardContent className="p-4">
          <AdvancedFilters
            onFiltersChange={handleGlobalFiltersChange}
            showSearch={false}
            showStatut={false}
            showCabinet={true}
            showRegion={true}
            showSecteur={true}
            showPeriode={true}
            compact={true}
          />
        </CardContent>
      </Card>

      {/* Comparison Mode Tabs */}
      <Tabs value={compareMode} onValueChange={setCompareMode} className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
          <TabsTrigger value="region" className="flex items-center gap-2">
            <Map className="w-4 h-4" />
            Par Région
          </TabsTrigger>
          <TabsTrigger value="period" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Par Période
          </TabsTrigger>
          <TabsTrigger value="secteur" className="flex items-center gap-2">
            <Factory className="w-4 h-4" />
            Par Secteur
          </TabsTrigger>
        </TabsList>

        {/* Region Comparison */}
        <TabsContent value="region" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <GitCompare className="w-5 h-5 text-primary" />
                Sélectionnez jusqu'à 4 régions à comparer
              </CardTitle>
              <CardDescription>
                Cliquez sur la carte ou cochez les régions ci-dessous
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Map */}
                <div>
                  <GuineaMap
                    regionsStats={stats?.regions || []}
                    onRegionClick={handleRegionClick}
                    selectedRegion={selectedRegionOnMap}
                  />
                </div>
                
                {/* Region Checkboxes */}
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground mb-4">
                    Régions sélectionnées: {selectedRegions.length}/4
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {REGIONS.map((region, idx) => (
                      <label
                        key={region}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedRegions.includes(region)
                            ? "border-primary bg-primary/5"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <Checkbox
                          checked={selectedRegions.includes(region)}
                          onCheckedChange={() => toggleRegion(region)}
                          disabled={!selectedRegions.includes(region) && selectedRegions.length >= 4}
                        />
                        <span className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                          />
                          {region}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Period Comparison */}
        <TabsContent value="period" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Sélectionnez jusqu'à 4 années à comparer
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {availableYears.length > 0 ? (
                  availableYears.map((year, idx) => (
                    <label
                      key={year}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedYears.includes(year.toString())
                          ? "border-primary bg-primary/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <Checkbox
                        checked={selectedYears.includes(year.toString())}
                        onCheckedChange={() => toggleYear(year.toString())}
                        disabled={!selectedYears.includes(year.toString()) && selectedYears.length >= 4}
                      />
                      <span className="flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                        />
                        {year}
                      </span>
                    </label>
                  ))
                ) : (
                  <p className="text-muted-foreground">Aucune année disponible</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sector Comparison */}
        <TabsContent value="secteur" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Factory className="w-5 h-5 text-primary" />
                Sélectionnez jusqu'à 4 secteurs à comparer
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {SECTEURS.map((secteur, idx) => (
                  <label
                    key={secteur}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all text-sm ${
                      selectedSecteurs.includes(secteur)
                        ? "border-primary bg-primary/5"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <Checkbox
                      checked={selectedSecteurs.includes(secteur)}
                      onCheckedChange={() => toggleSecteur(secteur)}
                      disabled={!selectedSecteurs.includes(secteur) && selectedSecteurs.length >= 4}
                    />
                    <span className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                      />
                      <span className="truncate">{secteur}</span>
                    </span>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Comparison Results */}
      {comparisonData.length > 0 && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {comparisonData.map((data, idx) => (
              <Card key={data.name} className="border-l-4" style={{ borderLeftColor: COLORS[idx % COLORS.length] }}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{data.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Effectif Total</span>
                    <span className="font-semibold">
                      {((data.total_guineens_hommes || 0) + (data.total_guineens_femmes || 0) + 
                        (data.total_etrangers_hommes || 0) + (data.total_etrangers_femmes || 0)).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">CDI / CDD</span>
                    <span className="font-semibold">{data.total_cdi || 0} / {data.total_cdd || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Taux Rotation</span>
                    <span className="font-semibold">{(data.avg_taux_rotation || 0).toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Masse Salariale</span>
                    <span className="font-semibold">
                      {((data.total_masse_salariale || 0) / 1000000).toFixed(0)}M
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Comparison Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Effectifs Comparison */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Comparaison des Effectifs</CardTitle>
                <CardDescription>Guinéens et Étrangers (Hommes/Femmes)</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={effectifsCompareData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Guinéens H" fill="#1B4D3E" />
                    <Bar dataKey="Guinéens F" fill="#D4AF37" />
                    <Bar dataKey="Étrangers H" fill="#64748B" />
                    <Bar dataKey="Étrangers F" fill="#0EA5E9" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Contrats Comparison */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Comparaison CDI / CDD</CardTitle>
                <CardDescription>Type de contrat par {compareMode === "region" ? "région" : compareMode === "period" ? "année" : "secteur"}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={contratsCompareData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="CDI" fill="#1B4D3E" />
                    <Bar dataKey="CDD" fill="#D4AF37" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Taux Comparison */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Comparaison des Taux</CardTitle>
                <CardDescription>Rotation et Absentéisme (%)</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={tauxCompareData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Taux Rotation" fill="#8B5CF6" />
                    <Bar dataKey="Taux Absentéisme" fill="#EC4899" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Categories Comparison */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Catégories Professionnelles</CardTitle>
                <CardDescription>Répartition par niveau</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={categoriesCompareData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Cadres" fill="#1B4D3E" />
                    <Bar dataKey="Agents Maîtrise" fill="#D4AF37" />
                    <Bar dataKey="Employés" fill="#0EA5E9" />
                    <Bar dataKey="Ouvriers" fill="#64748B" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Mouvements Comparison */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Mouvements de Personnel</CardTitle>
                <CardDescription>Embauches, Licenciements, Démissions</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={mouvementsCompareData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Embauches" fill="#22c55e" />
                    <Bar dataKey="Licenciements" fill="#ef4444" />
                    <Bar dataKey="Démissions" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Radar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Profil Comparatif</CardTitle>
                <CardDescription>Vue d'ensemble des indicateurs clés</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} />
                    {comparisonData.map((d, idx) => (
                      <Radar
                        key={d.name}
                        name={d.name}
                        dataKey={d.name}
                        stroke={COLORS[idx % COLORS.length]}
                        fill={COLORS[idx % COLORS.length]}
                        fillOpacity={0.2}
                      />
                    ))}
                    <Legend />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Comparison Table */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Tableau Comparatif Détaillé</CardTitle>
              <CardDescription>
                Comparaison par {compareMode === "region" ? "région" : compareMode === "period" ? "période" : "secteur"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left py-3 px-4 font-semibold">Indicateur</th>
                      {comparisonData.map((d, idx) => (
                        <th key={d.name} className="text-center py-3 px-4 font-semibold">
                          <span className="flex items-center justify-center gap-2">
                            <span
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                            />
                            {d.name}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-medium">Guinéens Hommes</td>
                      {comparisonData.map(d => (
                        <td key={d.name} className="text-center py-3 px-4">{d.total_guineens_hommes || 0}</td>
                      ))}
                    </tr>
                    <tr className="border-b bg-slate-50/50">
                      <td className="py-3 px-4 font-medium">Guinéens Femmes</td>
                      {comparisonData.map(d => (
                        <td key={d.name} className="text-center py-3 px-4">{d.total_guineens_femmes || 0}</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-medium">Étrangers Hommes</td>
                      {comparisonData.map(d => (
                        <td key={d.name} className="text-center py-3 px-4">{d.total_etrangers_hommes || 0}</td>
                      ))}
                    </tr>
                    <tr className="border-b bg-slate-50/50">
                      <td className="py-3 px-4 font-medium">Étrangers Femmes</td>
                      {comparisonData.map(d => (
                        <td key={d.name} className="text-center py-3 px-4">{d.total_etrangers_femmes || 0}</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-medium text-emerald-700">CDI</td>
                      {comparisonData.map(d => (
                        <td key={d.name} className="text-center py-3 px-4 font-semibold text-emerald-600">{d.total_cdi || 0}</td>
                      ))}
                    </tr>
                    <tr className="border-b bg-slate-50/50">
                      <td className="py-3 px-4 font-medium text-amber-700">CDD</td>
                      {comparisonData.map(d => (
                        <td key={d.name} className="text-center py-3 px-4 font-semibold text-amber-600">{d.total_cdd || 0}</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-medium">Cadres</td>
                      {comparisonData.map(d => (
                        <td key={d.name} className="text-center py-3 px-4">{d.total_cadres || 0}</td>
                      ))}
                    </tr>
                    <tr className="border-b bg-slate-50/50">
                      <td className="py-3 px-4 font-medium">Agents Maîtrise</td>
                      {comparisonData.map(d => (
                        <td key={d.name} className="text-center py-3 px-4">{d.total_agents_maitrise || 0}</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-medium">Employés</td>
                      {comparisonData.map(d => (
                        <td key={d.name} className="text-center py-3 px-4">{d.total_employes || 0}</td>
                      ))}
                    </tr>
                    <tr className="border-b bg-slate-50/50">
                      <td className="py-3 px-4 font-medium">Ouvriers</td>
                      {comparisonData.map(d => (
                        <td key={d.name} className="text-center py-3 px-4">{d.total_ouvriers || 0}</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-medium text-purple-700">Taux Rotation (%)</td>
                      {comparisonData.map(d => (
                        <td key={d.name} className="text-center py-3 px-4 font-semibold text-purple-600">
                          {(d.avg_taux_rotation || 0).toFixed(1)}%
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b bg-slate-50/50">
                      <td className="py-3 px-4 font-medium text-red-700">Taux Absentéisme (%)</td>
                      {comparisonData.map(d => (
                        <td key={d.name} className="text-center py-3 px-4 font-semibold text-red-600">
                          {(d.avg_taux_absenteisme || 0).toFixed(1)}%
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-medium">Embauches</td>
                      {comparisonData.map(d => (
                        <td key={d.name} className="text-center py-3 px-4 text-green-600">{d.total_embauches || 0}</td>
                      ))}
                    </tr>
                    <tr className="border-b bg-slate-50/50">
                      <td className="py-3 px-4 font-medium">Licenciements</td>
                      {comparisonData.map(d => (
                        <td key={d.name} className="text-center py-3 px-4 text-red-600">{d.total_licenciements || 0}</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-medium">Masse Salariale (GNF)</td>
                      {comparisonData.map(d => (
                        <td key={d.name} className="text-center py-3 px-4 font-semibold">
                          {((d.total_masse_salariale || 0) / 1000000).toFixed(0)}M
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {comparisonData.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <GitCompare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-semibold text-slate-700 mb-2">Aucune sélection</h3>
            <p className="text-muted-foreground">
              Sélectionnez au moins une {compareMode === "region" ? "région" : compareMode === "period" ? "année" : "secteur"} pour voir les statistiques comparatives
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
